import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { isAdminOrSuperAdmin } from '../utils/auth';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import '../App.css';

interface Tournament {
  id: string;
  name: string;
  format: string;
  status: string;
  startDate?: string;
  endDate?: string;
  teamCount: number;
  administratorId: string;
}

const formatLabels: Record<string, string> = {
  single_elimination: 'Single Elimination',
  double_elimination: 'Double Elimination',
  round_robin: 'Round Robin',
  group_knockout: 'Group + Knockout',
};

const statusLabels: Record<string, string> = {
  draft: 'Draft',
  registration: 'Registration',
  in_progress: 'In Progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

const statusColors: Record<string, string> = {
  draft: '#6c757d',
  registration: '#0d6efd',
  in_progress: '#198754',
  completed: '#6f42c1',
  cancelled: '#dc3545',
};

const Tournaments: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const isAdmin = isAdminOrSuperAdmin(user?.profileType);

  useEffect(() => {
    const fetchTournaments = async () => {
      setLoading(true);
      try {
        const params: Record<string, string> = { page: String(page), limit: '20' };
        if (statusFilter) params.status = statusFilter;
        const res = await api.get('/tournaments', { params });
        setTournaments(res.data.tournaments);
        setTotalPages(res.data.pagination.totalPages);
      } catch (err) {
        console.error('Error fetching tournaments:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchTournaments();
  }, [page, statusFilter]);

  if (loading) return <LoadingSpinner fullPage />;

  return (
    <div className="page-container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1>Tournaments</h1>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #dee2e6' }}
          >
            <option value="">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="registration">Registration</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
          </select>
          {isAdmin && (
            <button className="btn btn-primary" onClick={() => navigate('/tournaments/create')}>
              Create Tournament
            </button>
          )}
        </div>
      </div>

      {tournaments.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>
          <p>No tournaments found.</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '16px' }}>
          {tournaments.map((t) => (
            <div key={t.id} className="card" style={{ padding: '20px', borderRadius: '8px', border: '1px solid #dee2e6' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                <h3 style={{ margin: 0 }}>{t.name}</h3>
                <span style={{
                  padding: '2px 8px', borderRadius: '12px', fontSize: '12px', fontWeight: 600,
                  backgroundColor: statusColors[t.status] || '#6c757d', color: 'white'
                }}>
                  {statusLabels[t.status] || t.status}
                </span>
              </div>
              <p style={{ color: '#6c757d', margin: '4px 0' }}>
                {formatLabels[t.format] || t.format}
              </p>
              <p style={{ color: '#6c757d', margin: '4px 0' }}>
                {t.teamCount} team{t.teamCount !== 1 ? 's' : ''}
              </p>
              {t.startDate && (
                <p style={{ color: '#6c757d', margin: '4px 0', fontSize: '14px' }}>
                  {new Date(t.startDate).toLocaleDateString()}
                  {t.endDate && ` - ${new Date(t.endDate).toLocaleDateString()}`}
                </p>
              )}
              <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                <Link to={`/tournaments/${t.id}/bracket`} className="btn btn-sm btn-outline-primary">
                  Bracket
                </Link>
                {isAdmin && (
                  <Link to={`/tournaments/${t.id}/manage`} className="btn btn-sm btn-outline-secondary">
                    Manage
                  </Link>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '20px' }}>
          <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="btn btn-sm btn-outline-secondary">Previous</button>
          <span style={{ padding: '6px 12px' }}>Page {page} of {totalPages}</span>
          <button disabled={page >= totalPages} onClick={() => setPage(page + 1)} className="btn btn-sm btn-outline-secondary">Next</button>
        </div>
      )}
    </div>
  );
};

export default Tournaments;
